import { Switch } from "@chakra-ui/react";
import { useColorMode } from "@chakra-ui/system";
import hj from "@hotjar/browser";
import { useDynamicDrawerContext, useDynamicModalContext } from "@umami/components";
import { useCurrentAccount, useDefaultAccount, useImplicitAccounts } from "@umami/state";

import { AddressBookMenu } from "./AddressBookMenu/AddressBookMenu";
import { AppsMenu } from "./AppsMenu/AppsMenu";
import { ChangePasswordMenu } from "./ChangePasswordMenu/ChangePasswordMenu";
import { GenericMenu } from "./GenericMenu";
import { LogoutModal } from "./LogoutModal";
import {
  AlertCircleIcon,
  BookIcon,
  CodeSandboxIcon,
  DownloadIcon,
  LockIcon,
  LogoutIcon,
  MoonIcon,
  RadioIcon,
  ShieldIcon,
  SunIcon,
  UserPlusIcon,
} from "../../assets/icons";
import { OnboardOptionsModal } from "../Onboarding/OnboardOptions";
import { ErrorLogsMenu } from "./ErrorLogsMenu/ErrorLogsMenu";
import { NetworkMenu } from "./NetworkMenu/NetworkMenu";
import { usePasskey } from "../../hooks/usePasskey";
import { useHandleSaveBackup } from "../Onboarding/ImportWallet/useSaveBackup";

export const Menu = () => {
  const { openWith: openModal } = useDynamicModalContext();
  const { openWith: openDrawer } = useDynamicDrawerContext();
  const { colorMode, toggleColorMode } = useColorMode();
  const { isPasskeyFlow, isPasskeyUser } = usePasskey();
  const defaultAccount = useDefaultAccount();
  const implicitAccounts = useImplicitAccounts();
  const currentAccount = useCurrentAccount();
  const saveBackup = useHandleSaveBackup();

  const isLightColorMode = colorMode === "light";
  const colorModeSwitchLabel = isLightColorMode ? "Light mode" : "Dark mode";
  const ColorModeSwitchIcon = () => (isLightColorMode ? <SunIcon /> : <MoonIcon />);
  hj.stateChange("menu");

  const handleLockOut = () => {
    sessionStorage.clear();
    window.location.reload();
  };

  const addressBook = {
    label: "Address book",
    icon: <BookIcon />,
    onClick: async () => {
      await openDrawer(<AddressBookMenu />);
    },
    hasArrow: true,
  };
  const addAccount = {
    label: "Add account",
    icon: <UserPlusIcon />,
    onClick: async () => {
      await openModal(<OnboardOptionsModal />);
    },
    hasArrow: false,
  };
  const backup = {
    label: "Save backup",
    icon: <DownloadIcon />,
    onClick: saveBackup,
    hasArrow: false,
  };
  const apps = {
    label: "Apps",
    icon: <CodeSandboxIcon />,
    onClick: async () => {
      await openDrawer(<AppsMenu />);
    },
    hasArrow: true,
  };
  const changePassword = {
    label: "Password",
    icon: <ShieldIcon />,
    onClick: async () => {
      await openDrawer(<ChangePasswordMenu />);
    },
    hasArrow: true,
  };
  const errorLogs = {
    label: "Error logs",
    icon: <AlertCircleIcon />,
    onClick: async () => {
      await openDrawer(<ErrorLogsMenu />);
    },
  };
  const network = {
    label: "Network",
    icon: <RadioIcon />,
    onClick: async () => {
      await openDrawer(<NetworkMenu />);
    },
    hasArrow: true,
  };
  const lock = {
    label: "Lock Umami",
    icon: <LockIcon />,
    onClick: handleLockOut,
  };

  const signOut = {
    label: "Sign Out",
    icon: <LogoutIcon />,
    onClick: async () => {
      await openModal(<LogoutModal />);
    },
  };

  const coreMenuItems =
    isPasskeyFlow && isPasskeyUser
      ? [addAccount, addressBook, backup, apps, errorLogs]
      : [addAccount, addressBook, changePassword, backup, apps, errorLogs];

  // Hide the network menu for ledger accounts, as they are considered single chain for now
  if (currentAccount?.type !== "ledger") {
    coreMenuItems.splice(coreMenuItems.length - 1, 0, network);
  }

  const onlySocialAccountsExists =
    defaultAccount.type === "social" &&
    !implicitAccounts.filter(item => item.type !== "social").length;

  const filteredCoreMenuItems = onlySocialAccountsExists
    ? coreMenuItems.filter(item => item.label !== "Save backup")
    : coreMenuItems;

  const themeMenuItems = [
    {
      label: colorModeSwitchLabel,
      icon: <ColorModeSwitchIcon />,
      onClick: () => {
        toggleColorMode();
      },
      rightElement: (
        <Switch
          isChecked={isLightColorMode}
          onChange={() => {
            toggleColorMode();
          }}
        />
      ),
    },
  ];

  const logoutMenuItems = [lock, signOut];

  const menuItems = [filteredCoreMenuItems, themeMenuItems, logoutMenuItems];

  return <GenericMenu menuItems={menuItems} />;
};
